import { useCallback, useEffect, useRef, useState } from "react";

// Height of the strip a step has to cross to count as active. Thin enough
// that only one step is ever in it at rest, thick enough that a fast scroll
// doesn't jump clean over it between observer callbacks.
const ACTIVE_BAND_HEIGHT = 96;

// Drives an N-step sequence from scroll position: register one element per
// step, and the hook reports which one is currently level with the middle of
// the readable area. Steps can be any height, which an offset/progress
// calculation couldn't handle — copy length varies per step.
//
// `topOffset` is the height of anything pinned over the top of the viewport
// (a sticky nav, a sticky tab bar). The band is centred on the area *below*
// it rather than on the viewport, so the active step lines up with a pinned
// companion column that also starts below it. The margin is computed in px
// rather than percentages because it has to subtract that fixed offset from
// a viewport-relative centre.
//
// Returns a `registerStep(index)` ref callback rather than a fixed set of
// refs so the step count can come from data. `scrollToIndex` moves the page
// to a given step, for click-to-jump controls.
export const useScrollLinkedIndex = (
  count,
  { reducedMotion = false, topOffset = 0 } = {},
) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const stepRefs = useRef([]);

  const registerStep = useCallback(
    (index) => (node) => {
      stepRefs.current[index] = node;
    },
    [],
  );

  useEffect(() => {
    const nodes = stepRefs.current.slice(0, count).filter(Boolean);
    if (!nodes.length || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    let observer;

    const connect = () => {
      observer?.disconnect();

      const viewportHeight = window.innerHeight;
      const bandCentre = topOffset + (viewportHeight - topOffset) / 2;
      const marginTop = -(bandCentre - ACTIVE_BAND_HEIGHT / 2);
      const marginBottom = -(
        viewportHeight -
        bandCentre -
        ACTIVE_BAND_HEIGHT / 2
      );

      observer = new IntersectionObserver(
        (entries) => {
          // Fast scrolling can leave the band momentarily empty between two
          // steps, and can also deliver several entries at once. Take the
          // topmost element currently in the band and ignore empty frames,
          // which holds the last good value rather than flickering to 0.
          const inBand = entries
            .filter((entry) => entry.isIntersecting)
            .map((entry) => nodes.indexOf(entry.target))
            .filter((index) => index !== -1);
          if (!inBand.length) return;
          setActiveIndex(Math.min(...inBand));
        },
        {
          rootMargin: `${marginTop}px 0px ${marginBottom}px 0px`,
          threshold: 0,
        },
      );

      nodes.forEach((node) => observer.observe(node));
    };

    connect();
    // The margin is derived from viewport height, so it has to be rebuilt
    // when that changes — including the address-bar show/hide that fires
    // resize mid-scroll on mobile browsers.
    window.addEventListener("resize", connect);
    return () => {
      window.removeEventListener("resize", connect);
      observer?.disconnect();
    };
  }, [count, topOffset]);

  const scrollToIndex = useCallback(
    (index) => {
      const node = stepRefs.current[index];
      if (!node) return;
      // Give immediate feedback, then let the observer take over as the
      // scroll settles. Both agree once it lands, since we scroll to the
      // exact element the observer is watching.
      setActiveIndex(index);
      node.scrollIntoView({
        behavior: reducedMotion ? "auto" : "smooth",
        block: "center",
      });
    },
    [reducedMotion],
  );

  return { activeIndex, registerStep, scrollToIndex };
};
